import { notification } from '@arkestro/arkestro-design-system';
import { REQUEST_TYPE_PRICE_UPDATE } from '@components/bid_request/RequestConstants';
import Routes from '@services/Routes';
import { generateDownloadFromUrl } from 'utils/files';

const QUOTE_SUBMISSION_PATH = '/quote_submission';

export const ORGANIZATION_PATH = '/organization';

export const BID_SUBMISSION_PROCESSING_NOTIF_KEY = 'bid_submission_processing';
export const BID_SUBMISSION_SUBMITTED_NOTIF_KEY = 'bid_submitted';
const IGNORED_DATA = ['award_scenario', 'updated_award_scenario'];

const shouldIgnoreData = data => {
  return IGNORED_DATA.some(key => data[key]);
};
const eventBus = new EventTarget();

class PusherService {
  constructor (data, provider) {
    this.data = data;
    this.provider = provider;
    this.router = provider.props.router;
  }

  // type can be 'success', 'info', 'warning', 'error'
  static notifyClient = ({
    messageDescription,
    messageDuration,
    messageTitle,
    type = 'info',
    key = undefined
  }) => {
    notification[type]({
      description: messageDescription,
      duration: messageDuration,
      key,
      message: messageTitle
    });
  };

  //  Register a listener for a specific event name.
  //  Returns an unsubscribe function you can call in useEffect cleanup.
  static on (eventName, listener) {
    const handler = domEvent => listener(domEvent.detail);
    eventBus.addEventListener(eventName, handler);

    // Return a function that removes this listener
    return () => eventBus.removeEventListener(eventName, handler);
  }

  // Emit an event with a payload to all registered listeners.
  static emit (eventName, payload) {
    const event = new CustomEvent(eventName, { detail: payload });
    eventBus.dispatchEvent(event);
  }

  handleEvents = () => {
    if (!this.data || Object.keys(this.data).length === 0) return;

    const {
      messageTitle,
      messageDescription,
      messageDuration,
      messageType = 'success',
      key
    } = this.generateMessageInfo();

    if (messageTitle) {
      PusherService.notifyClient({
        key,
        messageDescription,
        messageDuration,
        messageTitle,
        type: messageType
      });
    }
  };

  generateMessageInfo = () => {
    const {
      job_error: jobError = {},
      cloned_bid_request: clonedRequest,
      bid_submission: bidSubmission,
      doc_parsed_bid_submission: docParsedBidSubmission,
      file_download: fileDownload,
      bid_request: bidRequest,
      created_award_scenario: createdAwardScenario,
      supplier_unawarded: supplierUnawarded
    } = this.data;
    const { t } = this.provider.props.i18n;
    const pathName = this.router.pathname;
    const isQuoteSubmissionPage = pathName.indexOf(QUOTE_SUBMISSION_PATH) > -1;
    const isOrganizationPage = pathName.indexOf(ORGANIZATION_PATH) > -1;
    const hasJobError = !!jobError?.message?.length;

    let messageType = 'success';

    let key;

    let messageTitle = t('general.notification');

    let messageDescription;

    let messageDuration = 8;

    if (bidSubmission) {
      this.provider.setState({
        bidSubmissionChangeCount: this.provider.state.bidSubmissionChangeCount + 1,
        submissionInfo: bidSubmission
      });

      notification.destroy(BID_SUBMISSION_PROCESSING_NOTIF_KEY);
      notification.destroy(BID_SUBMISSION_SUBMITTED_NOTIF_KEY);
    }

    if (hasJobError) {
      messageType = 'error';
      messageTitle = jobError.title || messageTitle;
      messageDescription = jobError.message;
    } else {
      // No op since we are updating the page directly, future work should update how we parse pusher data here
      if (shouldIgnoreData(this.data)) {
        return {
          messageTitle: null
        };
      }

      // Clone job
      if (clonedRequest) {
        const { id, name } = clonedRequest;

        if (this.provider.areCloneJobsComplete() ){
          this.provider.setGeneralProperty({ hasStaleData : true });
        }

        messageTitle = t('bid_request.event_ready');
        const text = t('bid_request.click_here_to_view_name', { name });

        messageDescription = (
          <a href={`/bid_requests/${id}/dashboard`} rel='noreferrer' target='_blank'>{text}</a>
        );
      }

      // Award Scenario Job
      if (createdAwardScenario?.id && bidRequest?.id) {
        const { id, title: name } = createdAwardScenario;

        messageTitle = t('bid_request.award_scenario_ready');
        messageDescription = (
          <a href={Routes.REQUEST_AWARD_SCENARIO__PATH(bidRequest.id, id)} rel='noreferrer' target='_blank'>
            {t('bid_request.view_name', { name })}
          </a>
        );
      }

      // Quote-by-doc messaging and stuff
      if (docParsedBidSubmission) {
        const {
          presigned_s3_url: docParsingBidSubmissionFileUrl,
          event_id: eventId,
          event_name: eventName
        } = docParsedBidSubmission;

        this.provider.setState({
          docParsingBidSubmissionFileUrl
        });

        messageDuration = 0; // It has to be closed

        const eventNameDisplay = eventName || t('common.unknown_event');
        messageTitle = t('buyer_quote_submissions.quote_doc_upload.title');
        messageDescription = t('buyer_quote_submissions.quote_doc_upload.other_page_message', {
          event_name: eventNameDisplay
        });

        if (eventId) {
          messageDescription = (
            <a
              href={`/bid_requests/${eventId}/quote_submission`}
              rel='noreferrer'
              target='_blank'
            >
              {messageDescription}
            </a>
          );
        }
      }

      // Bid Submission Job
      if (bidSubmission) {
        const {
          request,
          round_no: roundNo,
          bid_type: bidType,
          is_answer_only_submission: isAnswerOnlySubmission,
          bid_response: bidResponse
        } = bidSubmission;
        const { id, name, request_type: requestType } = request;
        const isPriceUpdate = requestType === REQUEST_TYPE_PRICE_UPDATE;
        const isDraftSubmission = ['Draft', 'draft'].includes(bidType);
        const wasSubmittedByNonVendor = !!bidResponse?.modified_by_id;

        key = BID_SUBMISSION_SUBMITTED_NOTIF_KEY;

        if (wasSubmittedByNonVendor) {
          messageTitle = t('bid_response.bid_submission.submitted');
          messageDescription = t('bid_response.bid_submission.submission_modified_description');
        } else if (isAnswerOnlySubmission) {
          messageTitle = t('bid_response.bid_submission.answers_submitted');
          messageDescription = t('bid_response.bid_submission.line_item_answers_have_been_submitted');
        } else {
          messageTitle = isPriceUpdate
            ? t('bid_response.bid_submission.update_success_title')
            : t('bid_response.bid_submission.submitted');
          messageDescription = (
            <a href={`/bid_requests/${id}/quote_submission`} rel='noreferrer' target='_blank'>
              <div style={{ color: '#333333' }}>
                <div><strong>{t('general.request_name')}</strong>: {name}</div>
                {!isPriceUpdate && roundNo > 0 && (
                  <div><strong>{t('general.round_no')}</strong>: {roundNo}</div>
                )}
                <div><strong>{t('bid_response.bid_submission.type')}</strong>: {bidType}</div>
              </div>
              <p>
                <em>{t('bid_response.bid_submission.click_here_to_view_submission')}</em>
              </p>
            </a>
          );

          if (isDraftSubmission) {
            messageTitle = t('bid_response.bid_submission.saved_draft');
            messageDescription = t('bid_response.bid_submission.saved_draft_description');
          }
        }

        // If a user is currently on the bid submission page when the job completes, we don't want
        // to spam them with both a modal "success" message and a notification "success" message.
        // Only show them them the notification that the job is complete if they are on a different
        // page.
        if (!hasJobError && isQuoteSubmissionPage) return;
      }

      // File Download Job
      if (fileDownload) {
        const { url, status } = fileDownload;

        // Let components know the file download status has changed
        PusherService.emit('file_download', fileDownload);

        if (status === 'completed') {
          messageTitle = t('general.file_download_start_soon');
          messageDescription = (
            <a download href={url}>
              {t('general.file_download_click')}
            </a>
          );
          generateDownloadFromUrl(url);
        }
        else {
          messageType = 'error';
          messageTitle = t('general.file_download_error');
          messageDescription = (
            <p style={{ color: '#333333' }}>
              <div><strong>{t('general.file_download_error_message')}</strong></div>
            </p>
          );
        }
      }

      // Supplier Unawarded Job
      if (supplierUnawarded) {
        const { bid_request_id: bidRequestId } = supplierUnawarded;

        this.provider.setGeneralProperty({
          displayModalForUnawardedRequests: {
            ...this.provider.state.displayModalForUnawardedRequests,
            [bidRequestId]: true
          }
        });
        return {};
      }
    }

    return {
      key,
      messageDescription,
      messageDuration,
      messageTitle,
      messageType
    };
  };
}

export default PusherService;
